function defineSaB2bBenefits() {
  if (customElements.get('sa-b2b-benefits')) return true;
  if (typeof PopupBase === 'undefined') return false;

  class SaB2bBenefits extends PopupBase {
    constructor() {
      super();
      this.activeTrigger = null;
      this.templates = new Map();
      this.triggers = [];
      this.closeButtons = [];
      this.inertedElements = [];
      this.tabletCardHeightMediaQuery = null;
      this.tabletCardHeightResizeObserver = null;
      this.tabletCardHeightRaf = null;
      this.copyBaselineAlignmentRaf = null;
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleTabletWindowResize = this.handleTabletWindowResize.bind(this);
      this.handleTabletMediaChange = this.handleTabletMediaChange.bind(this);
      this.handleTabletGridResize = this.handleTabletGridResize.bind(this);
      this.handleWindowResize = this.handleWindowResize.bind(this);
      this.scheduleTabletCardHeightsSync = this.scheduleTabletCardHeightsSync.bind(this);
      this.syncTabletCardHeights = this.syncTabletCardHeights.bind(this);
      this.scheduleCopyBaselineAlignment = this.scheduleCopyBaselineAlignment.bind(this);
      this.syncCopyBaselineAlignment = this.syncCopyBaselineAlignment.bind(this);
    }

    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) this.initialize();
      });
    }

    initialize() {
      if (this.initialized) return;

      this.customClass = this.dataset.customClass || 'sa-b2b-benefits-modal';
      this.templates = new Map(
        Array.from(this.querySelectorAll('[data-sa-b2b-benefits-template]')).map((template) => [
          template.dataset.saB2bBenefitsTemplate,
          template,
        ])
      );
      this.triggers = Array.from(this.querySelectorAll('[data-sa-b2b-benefits-trigger]'));

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTriggerClick);
      });
      window.addEventListener('resize', this.handleWindowResize, { passive: true });
      this.setupTabletCardHeights();
      this.scheduleCopyBaselineAlignment();
      if (document.fonts?.ready) {
        document.fonts.ready.then(this.scheduleCopyBaselineAlignment);
      }
      this.initialized = true;
    }

    setupTabletCardHeights() {
      this.teardownTabletCardHeights();

      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

      this.tabletCardHeightMediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 991.98px)');
      if (!this.tabletCardHeightMediaQuery.matches) return;

      window.addEventListener('resize', this.handleTabletWindowResize, { passive: true });
      if (typeof this.tabletCardHeightMediaQuery.addEventListener === 'function') {
        this.tabletCardHeightMediaQuery.addEventListener('change', this.handleTabletMediaChange);
      } else if (typeof this.tabletCardHeightMediaQuery.addListener === 'function') {
        this.tabletCardHeightMediaQuery.addListener(this.handleTabletMediaChange);
      }

      if (typeof ResizeObserver === 'function') {
        this.tabletCardHeightResizeObserver = new ResizeObserver(this.handleTabletGridResize);
        const grid = this.querySelector('.sa-b2b-procurement-benefits__grid');
        if (grid) this.tabletCardHeightResizeObserver.observe(grid);
      }

      this.scheduleTabletCardHeightsSync();

      if (document.fonts?.ready) {
        document.fonts.ready.then(this.scheduleTabletCardHeightsSync);
      }
    }

    teardownTabletCardHeights() {
      window.removeEventListener('resize', this.handleTabletWindowResize);

      if (this.tabletCardHeightMediaQuery) {
        if (typeof this.tabletCardHeightMediaQuery.removeEventListener === 'function') {
          this.tabletCardHeightMediaQuery.removeEventListener('change', this.handleTabletMediaChange);
        } else if (typeof this.tabletCardHeightMediaQuery.removeListener === 'function') {
          this.tabletCardHeightMediaQuery.removeListener(this.handleTabletMediaChange);
        }
      }

      if (this.tabletCardHeightResizeObserver) {
        this.tabletCardHeightResizeObserver.disconnect();
        this.tabletCardHeightResizeObserver = null;
      }

      if (this.tabletCardHeightRaf) {
        cancelAnimationFrame(this.tabletCardHeightRaf);
        this.tabletCardHeightRaf = null;
      }

      if (this.copyBaselineAlignmentRaf) {
        cancelAnimationFrame(this.copyBaselineAlignmentRaf);
        this.copyBaselineAlignmentRaf = null;
      }

      const grid = this.querySelector('.sa-b2b-procurement-benefits__grid');
      grid?.style.removeProperty('--sa-b2b-procurement-benefits-card-equalized-height');
    }

    disconnectedCallback() {
      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.handleTriggerClick);
      });
      this.closeButtons.forEach((button) => {
        button.removeEventListener('click', this.handleCloseClick);
      });
      window.removeEventListener('resize', this.handleWindowResize);
      this.teardownTabletCardHeights();

      const modal = this.modal;
      this.modal = null;
      if (typeof modal?.close === 'function') modal.close();
      this.restoreBackgroundInert();

      this.activeTrigger = null;
      this.templates.clear();
      this.triggers = [];
      this.closeButtons = [];
      this.inertedElements = [];
      this.initialized = false;
    }

    handleTriggerClick(event) {
      event.preventDefault();

      const trigger = event.currentTarget;
      const popupKey = trigger.dataset.popupKey;
      const template = this.templates.get(popupKey);
      if (!template || this.modal) return;

      this.activeTrigger = trigger;
      this.setTriggerState(true);

      const content = document.createElement('div');
      content.appendChild(template.content.cloneNode(true));

      this.initPopup(content, null, this.customClass, (modalBox) => {
        this.configureDialog(modalBox);
      });
    }

    configureDialog(modalBox) {
      const dialog = modalBox.querySelector('[data-sa-b2b-benefits-dialog]');
      const title = modalBox.querySelector('[data-sa-b2b-benefits-title]');
      const modalRoot = modalBox.closest('.tingle-modal');
      const tingleCloseButton = modalRoot?.querySelector('.tingle-modal__close');
      const closeButton = modalBox.querySelector('[data-sa-b2b-benefits-close]');
      const popupLabel = dialog?.dataset.popupLabel;
      const closeLabel = dialog?.dataset.closeLabel;

      if (modalRoot) {
        const sourceStyles = getComputedStyle(this);
        [
          '--sa-b2b-procurement-benefits-modal-width-pc',
          '--sa-b2b-procurement-benefits-modal-width-m',
          '--sa-b2b-procurement-benefits-modal-content-padding-y-pc',
          '--sa-b2b-procurement-benefits-modal-content-padding-x-pc',
          '--sa-b2b-procurement-benefits-modal-content-padding-y-m',
          '--sa-b2b-procurement-benefits-modal-content-padding-x-m',
          '--sa-b2b-procurement-benefits-popup-header-max-width',
          '--sa-b2b-procurement-benefits-popup-close-size',
          '--sa-b2b-procurement-benefits-popup-title-size-pc',
          '--sa-b2b-procurement-benefits-popup-title-size-m',
          '--sa-b2b-procurement-benefits-popup-description-size',
          '--sa-b2b-procurement-benefits-popup-description-line-height-m',
          '--sa-b2b-procurement-benefits-popup-note-size-pc',
          '--sa-b2b-procurement-benefits-popup-note-size-m',
          '--sa-b2b-procurement-benefits-popup-note-shrink-m',
          '--sa-b2b-procurement-benefits-popup-table-gap-m',
          '--sa-b2b-procurement-benefits-popup-column-width-pc',
          '--sa-b2b-procurement-benefits-popup-column-width-m',
        ].forEach((name) => {
          const value = sourceStyles.getPropertyValue(name).trim();
          if (value) modalRoot.style.setProperty(name, value);
        });
      }

      if (dialog?.id) {
        modalBox.id = dialog.id;
        dialog.removeAttribute('id');
      }

      modalBox.setAttribute('role', 'dialog');
      modalBox.setAttribute('aria-modal', 'true');

      if (title?.id) {
        modalBox.setAttribute('aria-labelledby', title.id);
      } else if (popupLabel) {
        modalBox.setAttribute('aria-label', popupLabel);
      } else {
        const triggerLabel = this.activeTrigger?.innerText.trim();
        if (triggerLabel) modalBox.setAttribute('aria-label', triggerLabel);
      }

      if (modalBox.id && this.activeTrigger) {
        this.activeTrigger.setAttribute('aria-controls', modalBox.id);
      }

      this.setBackgroundInert(modalRoot);

      if (tingleCloseButton) {
        // PopupBase/Tingle still owns the lifecycle close method. Its generated
        // visual control is hidden because this section renders the Figma icon
        // and button in the popup header.
        tingleCloseButton.setAttribute('aria-hidden', 'true');
        tingleCloseButton.setAttribute('tabindex', '-1');
        tingleCloseButton.hidden = true;
      }

      if (closeButton) {
        const accessibleCloseLabel = closeLabel || window.accessibilityStrings?.close;
        if (accessibleCloseLabel) closeButton.setAttribute('aria-label', accessibleCloseLabel);
        closeButton.addEventListener('click', this.handleCloseClick);
        this.closeButtons.push(closeButton);
      }

      if (this._focusTrapCleanup) this._focusTrapCleanup();
      this._focusTrapCleanup = typeof trapFocus === 'function' ? trapFocus(modalBox) : null;
    }

    handleCloseClick(event) {
      event.preventDefault();
      if (this.modal) this.onClose();
    }

    handleTabletWindowResize() {
      this.scheduleCopyBaselineAlignment();
      this.scheduleTabletCardHeightsSync();
    }

    handleWindowResize() {
      this.scheduleCopyBaselineAlignment();
    }

    handleTabletMediaChange() {
      if (this.tabletCardHeightMediaQuery?.matches) {
        this.setupTabletCardHeights();
      } else {
        this.teardownTabletCardHeights();
      }
      this.scheduleCopyBaselineAlignment();
    }

    handleTabletGridResize() {
      this.scheduleTabletCardHeightsSync();
    }

    scheduleTabletCardHeightsSync() {
      if (this.tabletCardHeightRaf) return;

      this.tabletCardHeightRaf = requestAnimationFrame(() => {
        this.tabletCardHeightRaf = null;
        this.syncTabletCardHeights();
      });
    }

    scheduleCopyBaselineAlignment() {
      if (this.copyBaselineAlignmentRaf) return;

      this.copyBaselineAlignmentRaf = requestAnimationFrame(() => {
        this.copyBaselineAlignmentRaf = null;
        this.syncCopyBaselineAlignment();
      });
    }

    syncTabletCardHeights() {
      if (!this.isConnected || !this.tabletCardHeightMediaQuery?.matches) return;

      const grid = this.querySelector('.sa-b2b-procurement-benefits__grid');
      if (!grid) return;

      const cards = Array.from(grid.querySelectorAll('.sa-b2b-procurement-benefits__card'));
      if (!cards.length) return;

      let maxHeight = 0;
      cards.forEach((card) => {
        maxHeight = Math.max(maxHeight, card.getBoundingClientRect().height);
      });

      const nextHeight = `${Math.ceil(maxHeight)}px`;
      const currentHeight = grid.style.getPropertyValue('--sa-b2b-procurement-benefits-card-equalized-height').trim();
      if (currentHeight === nextHeight) return;

      grid.style.setProperty('--sa-b2b-procurement-benefits-card-equalized-height', nextHeight);
    }

    getTextLineInfo(element) {
      if (!element) return { lineCount: 1, lineHeight: 0 };

      const rects = Array.from(element.getClientRects());
      const computedStyle = getComputedStyle(element);
      const parsedLineHeight = parseFloat(computedStyle.lineHeight);
      const lineHeight = Number.isNaN(parsedLineHeight) ? null : parsedLineHeight;
      if (!lineHeight) return { lineCount: 1, lineHeight: 0 };

      if (rects.length > 1) {
        return {
          lineCount: rects.length,
          lineHeight,
        };
      }

      const lineCount = Math.max(
        1,
        Math.round(element.getBoundingClientRect().height / lineHeight),
      );

      return {
        lineCount,
        lineHeight,
      };
    }

    syncCopyBaselineAlignment() {
      if (!this.isConnected) return;

      const cardElements = Array.from(this.querySelectorAll('.sa-b2b-procurement-benefits__card'));
      if (!cardElements.length) {
        this.classList.remove('sa-b2b-procurement-benefits--copy-baseline-equalized');
        cardElements.forEach((card) => {
          card.classList.remove('sa-b2b-procurement-benefits__card--title-baseline-equalized');
          card.style.removeProperty('--sa-b2b-procurement-benefits-title-min-height');
        });
        this.style.removeProperty('--sa-b2b-procurement-benefits-title-min-height');
        return;
      }

      const isTabletAlignment = !!this.tabletCardHeightMediaQuery?.matches;

      cardElements.forEach((card) => {
        card.classList.remove('sa-b2b-procurement-benefits__card--title-baseline-equalized');
        card.style.removeProperty('--sa-b2b-procurement-benefits-title-min-height');
      });

      if (isTabletAlignment) {
        this.classList.remove('sa-b2b-procurement-benefits--copy-baseline-equalized');
        this.style.removeProperty('--sa-b2b-procurement-benefits-title-min-height');

        let shouldKeepClass = false;
        for (let i = 0; i < cardElements.length; i += 2) {
          const rowCards = cardElements.slice(i, i + 2);
          if (!rowCards.length) continue;

          let rowNeedsAlignment = false;
          let maxTitleLineCount = 1;
          let maxTitleLineHeight = 0;

          rowCards.forEach((card) => {
            const title = card.querySelector('.sa-b2b-procurement-benefits__title');
            const { lineCount, lineHeight } = this.getTextLineInfo(title);

            maxTitleLineCount = Math.max(maxTitleLineCount, lineCount);
            if (lineHeight) maxTitleLineHeight = Math.max(maxTitleLineHeight, lineHeight);
            if (lineCount >= 2) rowNeedsAlignment = true;
          });

          if (rowNeedsAlignment && maxTitleLineHeight > 0) {
            shouldKeepClass = true;
            const rowMinHeight = `${(maxTitleLineHeight * maxTitleLineCount).toFixed(2)}px`;
            rowCards.forEach((card) => {
              card.classList.add('sa-b2b-procurement-benefits__card--title-baseline-equalized');
              card.style.setProperty('--sa-b2b-procurement-benefits-title-min-height', rowMinHeight);
            });
          }
        }

        if (shouldKeepClass) return;
      }

      let maxTitleLineCount = 1;
      let maxTitleLineHeight = 0;

      const needsAlignment = cardElements.some((card) => {
        const title = card.querySelector('.sa-b2b-procurement-benefits__title');
        const { lineCount, lineHeight } = this.getTextLineInfo(title);

        maxTitleLineCount = Math.max(maxTitleLineCount, lineCount);
        if (lineHeight) maxTitleLineHeight = Math.max(maxTitleLineHeight, lineHeight);

        return lineCount >= 2;
      });

      if (needsAlignment && maxTitleLineHeight > 0) {
        this.classList.add('sa-b2b-procurement-benefits--copy-baseline-equalized');
        this.style.setProperty(
          '--sa-b2b-procurement-benefits-title-min-height',
          `${(maxTitleLineHeight * maxTitleLineCount).toFixed(2)}px`,
        );
      } else {
        this.classList.remove('sa-b2b-procurement-benefits--copy-baseline-equalized');
        this.style.removeProperty('--sa-b2b-procurement-benefits-title-min-height');
      }
    }

    setTriggerState(isExpanded) {
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(isExpanded && trigger === this.activeTrigger));
      });
    }

    setBackgroundInert(modalRoot) {
      this.restoreBackgroundInert();
      if (!modalRoot || !document.body) return;

      this.inertedElements = Array.from(document.body.children)
        .filter((element) => element !== modalRoot && !element.contains(modalRoot))
        .map((element) => ({
          element,
          hadInert: element.hasAttribute('inert'),
          hadAriaHidden: element.hasAttribute('aria-hidden'),
          ariaHidden: element.getAttribute('aria-hidden'),
        }));

      this.inertedElements.forEach(({ element }) => {
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
      });
    }

    restoreBackgroundInert() {
      this.inertedElements.forEach(({ element, hadInert, hadAriaHidden, ariaHidden }) => {
        if (!element?.isConnected) return;
        if (hadInert) element.setAttribute('inert', '');
        else element.removeAttribute('inert');
        if (hadAriaHidden) element.setAttribute('aria-hidden', ariaHidden ?? '');
        else element.removeAttribute('aria-hidden');
      });
      this.inertedElements = [];
    }

    onCloseEvent() {
      const triggerToRestore = this.activeTrigger;
      this.closeButtons.forEach((button) => {
        button.removeEventListener('click', this.handleCloseClick);
      });
      this.closeButtons = [];
      this.setTriggerState(false);
      if (this.activeTrigger?.hasAttribute('aria-controls')) {
        this.activeTrigger.removeAttribute('aria-controls');
      }
      this.restoreBackgroundInert();
      this.activeTrigger = null;
      this.modal = null;

      if (triggerToRestore?.isConnected) {
        requestAnimationFrame(() => {
          if (!this.activeTrigger) triggerToRestore.focus({ preventScroll: true });
        });
      }
    }
  }

  customElements.define('sa-b2b-benefits', SaB2bBenefits);
  return true;
}

function initializeSaB2bBenefits() {
  if (defineSaB2bBenefits()) return;

  const retryAfterGlobal = () => {
    if (defineSaB2bBenefits()) {
      window.removeEventListener('load', retryAfterGlobal);
    }
  };

  if (document.readyState === 'complete') {
    queueMicrotask(retryAfterGlobal);
  } else {
    window.addEventListener('load', retryAfterGlobal, { once: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSaB2bBenefits, { once: true });
} else {
  initializeSaB2bBenefits();
}
